// 單欄位驗證器
export class FieldValidator<T> {
    private validators: ((value: T, form?: any) => string | null)[] = [];

    /**
     * @description 必填驗證
     * @param msg 錯誤訊息
     */
    required(msg = '此欄位為必填') {
        this.validators.push((value: T) => {
            if (value === null || value === undefined || value === '') {
                return msg;
            }
            return null;
        });
        return this;
    }
    /**
     * @description 驗證值不可為0或是NaN
     * @param msg 錯誤訊息
     */
    rejectZero(msg = '此欄位為必填') {
        this.validators.push((value: T) => {
            const numValue = Number(value)
            if (numValue === 0 || Number.isNaN(numValue)) {
                return msg;
            }
            return null;
        });
        return this;
    }
    /**
     * @description 檔案必填驗證
     * @param msg 錯誤訊息
     */
    requiredFile(msg = '尚未選擇檔案') {
        this.validators.push((value: T) => {
            if (value === null || value === undefined || value === '') {
                return msg;
            }
            return null;
        });
        return this;
    }

    /**
     * @description email 格式驗證
     * @param msg 錯誤訊息
     */
    isEmail(msg = '不是合法的 email 格式') {
        this.validators.push((value: T) => {
            if (typeof value === 'string' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                return msg;
            }
            return null;
        });
        return this;
    }

    /**
     * @description 最小長度驗證
     * @param min 最小長度
     * @param msg 錯誤訊息
     */
    minLength(min: number, msg?: string) {
        this.validators.push((value: T) => {
            if (typeof value === 'string' && value.length < min) {
                return msg ?? `至少輸入 ${min} 個字`;
            }
            return null;
        });
        return this;
    }

    /**
     * @description 最大長度驗證
     * @param max 最大長度
     * @param msg 錯誤訊息
     */
    maxLength(max: number, msg?: string) {
        this.validators.push((value: T) => {
            if (typeof value === 'string' && value.length > max) {
                return msg ?? `最多輸入 ${max} 個字`;
            }
            return null;
        });
        return this;
    }

    /**
     * @description 最小數值驗證
     * @param min 最小數值
     * @param msg 錯誤訊息
     */
    minNumber(min: number, msg?: string) {
        this.validators.push((value: T) => {
            if (!Number.isNaN(Number(value)) && Number(value) < min) {
                return msg ?? `至少輸入 ${min}`;
            }
            return null;
        });
        return this;
    }

    /**
     * @description 最大數值驗證
     * @param max 最大數值
     * @param msg 錯誤訊息
     */
    maxNumber(max: number, msg?: string) {
        this.validators.push((value: T) => {
            if (!Number.isNaN(Number(value)) && Number(value) > max) {
                return msg ?? `最多輸入 ${max}`;
            }
            return null;
        });
        return this;
    }

    /**
     * @description 日期時間範圍檢查, 開始日期時間不可大於結束日期時間
     * @param startDateField 開始日期時間欄位名稱
     * @param endDateField 結束日期時間欄位名稱
     * @param msg 錯誤訊息
     */
    checkDateTimeRange(startDateField: string, endDateField: string, msg = '時間起不可大於時間訖') {
        this.validators.push((value: T, form?: any) => {
            if (form && startDateField in form && endDateField in form) {
                const startDate = form[startDateField];
                const endDate = form[endDateField];
                if (startDate && endDate && startDate > endDate) {
                    return msg;
                }
            }
            return null;
        });
        return this;
    }

    /**
     * @description 自訂驗證
     * @param validator 驗證函式
     * @param msg 錯誤訊息
     */
    custom(validator: (value: T, form: any) => boolean, msg: string) {
        this.validators.push((value: T, form?: any) => {
            return validator(value, form) ? null : msg;
        });
        return this;
    }

    validate(value: T, form?: any): string | null {
        for (const validator of this.validators) {
            const result = validator(value, form);
            if (result) return result;
        }
        return null;
    }
}

// 工廠函數：建立欄位驗證器（推斷型別）
export function field<T = any>(): FieldValidator<T> {
    return new FieldValidator<T>();

}

// 工廠函數：建立欄位驗證器（推斷型別）
export type FieldOrNestedSchema<T> =
    T extends FieldValidator<any> ? T :
    T extends object ? ValidatorSchema<T> :
    FieldValidator<T>;

// Partial schema：允許只定義需要驗證的欄位
export type ValidatorSchema<T> = Partial<{
    [K in keyof T]: FieldOrNestedSchema<T[K]>;
}>;

// 建立 schema（會推斷型別）
export function createSchema<T>(schema: ValidatorSchema<T>): ValidatorSchema<T> {
    return schema;
}

/**
 * @description 驗證器，用於驗證整個物件的欄位
 * @return 回傳檢查失敗的項目及對應錯誤訊息的物件 {[欄位名稱]:"錯誤訊息"}
 * @example
 * //要檢查的物件的型別
 * interface MyForm {
 *  email: string;
 *  username: string;
 *  age: number;
 * }
 * 
 * const myForm: MyForm = {
 *  email: '',
 *  username: 'ab',
 *  age: 10
 * }
 *
 * //定義欄位檢查項目
 * const schema = createSchema<MyForm>({
 *  email: field().required().isEmail(),
 *  username: field().required().minLength(3),
 *  age: field().custom(age => age >= 18, '年齡太小'),
 * });
 *
 * const validator = new Validator(schema);
 *
 * //檢查項目並獲取錯誤訊息
 * const errors = validator.validate(myForm);
 *
 * //輸出
 * //errors: { email: '此欄位為必填', username: '至少輸入 3 個字', age: '年齡太小' }
 * 
 * //如果有巢狀物件，schema可以這樣定義：
 * interface MyForm {
 *   email: string;
 *   profile: {
 *     name: string;
 *     age: number;
 *   };
 * }
 *
 * const schema = createSchema<MyForm>({
 *   email: field().required().isEmail(),
 *   profile: createSchema({
 *     name: field().required(),
 *     age: field().custom((val) => val > 0, '年齡必須大於 0')
 *   })
 * });
 * 
 * //動態成巢狀schima的範例
 * const nestedSchema = arr.reduce<Record<string, FieldValidator<any>>>((pre, currItem) => {
 *   if (currItem.is_require === "Y") {
 *       pre[currItem.id] = field().required();
 *   }
 *   return pre;
 * }, {});
 */
export class Validator<T extends Record<string, any>> {
    constructor(private schema: ValidatorSchema<T>) { }

    validate(data: T): Record<string, any> {
        const errors: Record<string, any> = {};

        for (const key in this.schema) {
            const validator = this.schema[key];
            if (!validator) continue;

            const value = data[key];

            // 如果是 FieldValidator
            if (
                typeof validator === 'object' &&
                validator !== null &&
                'validate' in (validator as object) &&
                typeof (validator as any).validate === 'function'
            ) {
                const error = (validator as FieldValidator<any>).validate(value, data);
                if (error) {
                    errors[key] = error;
                }
            } else if (typeof validator === 'object' && validator !== null) {
                // 巢狀 schema
                const nestedValidator = new Validator(validator as ValidatorSchema<any>);
                const nestedErrors = nestedValidator.validate(value || {});
                if (Object.keys(nestedErrors).length > 0) {
                    errors[key] = nestedErrors;
                }
            }
        }

        return errors;
    }
}

/**
 * @description 驗證器，包裝重複定義Validator的func，驗證推薦使用這個方法驗證
 * @param schema 驗證項目的 schema
 * @param data 要驗證的物件
 * @param handleCallbackErrors 用於返回錯誤訊息的函數 ({[key:string]:string})=>void
 * @returns boolean 是否有錯誤
 * @example
 * 
 * //定義欄位檢查項目
 * const schema = createSchema<MyForm>({
 *  username: field().required().minLength(3),
 * });
 * 
 * //定義巢狀schema，或動態欄位schema請參考Validator
 * 
 * //檢查欄位是否有錯誤，handleCallbackErrors可以獲取錯誤訊息
 * const isValid = validateData(schema, data, handleCallbackErrors)
 */
export function validateData<T extends Record<string, any>>(
    schema: ValidatorSchema<T>,
    data: T,
    handleCallbackErrors?: (errors: Record<string, string>) => void
): boolean {
    const validator = new Validator(schema);
    const formErrors = validator.validate(data);
    if (handleCallbackErrors) {
        handleCallbackErrors(formErrors);
    }

    return Object.keys(formErrors).length === 0;
}

/**
 * @description 將物件展平，將所有巢狀物件的鍵值對展平到頂層
 * @param obj 要展平的物件
 * @example
 * const nestedObj = {
 * parent1:{child1: 'value1', child2: 'value2'},
 * parent2: {child3: 'value3', child4: {subChild: 'value4'}},
 * }
 * 
 * const flatObj = flattenLeafKeys(nestedObj);
 * 
 * //輸出結果：
 *  flatObj: { child1: 'value1', child2: 'value2', child3: 'value3', subChild: 'value4' }
 */
export function flattenLeafKeys(obj: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};

    function recurse(current: any) {
        for (const key in current) {
            if (!current.hasOwnProperty(key)) continue;

            const value = current[key];
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                recurse(value);
            } else {
                result[key] = value;
            }
        }
    }

    recurse(obj);
    return result;
}